import React, { useEffect, useRef, useCallback } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Tooltip from "@mui/material/Tooltip";
import usePagination from "../../hooks/usePagination";
import { useCurrentStore } from "../../hooks/useCurrentStore";

/* ── Badge helpers ── */
const financialLabel = (status: string) => {
  switch (status) {
    case "paid":               return { text: "Paid",           cls: "obadge--paid" };
    case "partially_refunded": return { text: "Part. refunded", cls: "obadge--part-refunded" };
    case "refunded":           return { text: "Refunded",       cls: "obadge--refunded" };
    case "pending":            return { text: "Pending",        cls: "obadge--pending" };
    default:                   return { text: status,           cls: "obadge--default" };
  }
};

const fulfilmentLabel = (status: string) => {
  switch (status) {
    case "fulfilled": return { text: "Fulfilled", cls: "obadge--fulfilled" };
    case "partial":   return { text: "Partial",   cls: "obadge--partial" };
    case "success":   return { text: "Fulfilled", cls: "obadge--fulfilled" };
    default:          return {};
  }
};
  const toTitleCase = (str: string) =>
  str?.split(" ").map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");

const typeLabel = (type: string, storeName?: string) => {
  switch (type) {
    case "hybrid": return { text: `${toTitleCase(storeName ?? "Store")} / Hybrid`, cls: "obadge--hybrid" };
    case "store":  return { text: toTitleCase(storeName ?? "Store"), };
    case "direct": return { text: "Direct", cls: "obadge--direct" };
    default:       return {};
  }
};

const CANCELLED_LATER_TOOLTIP = "Later: Your customer won't be refunded. Refund the amount owed at a later point.";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });

const customerName = (order: any) => {
  const name = [order.customer_first_name, order.customer_last_name]
    .filter(Boolean)
    .join(" ");

  return name || "—";
};

interface Props { storeId?: string; onCommissionData?: (data: any) => void; }

const OrdersWrapper: React.FC<Props> = ({ storeId, onCommissionData }) => {
    
const currentRole = JSON.parse(localStorage.getItem("user") || '{}').current_role;


const isAdmin = currentRole === 1;

const userStoreId = useCurrentStore(storeId);

// base URL never includes store_id directly — cursor-pagination hook builds
// the query string from the body object passed to fetchInitial/fetchMore
const baseUrl = `${import.meta.env.VITE_API_URL}/orders`;
const needsStoreId = !(isAdmin && !storeId);

const {
  data: orders,
  responseMeta,
  errorContent,
  hasMore,
  isLoading,
  isLoadingMore,
  hasFetched,
  fetchInitial,
  fetchMore,
} = usePagination({ url: baseUrl, method: "GET", listKey: "orders" });

useEffect(() => {
  if (needsStoreId && !userStoreId) return; // wait until store id resolves
  fetchInitial(needsStoreId ? { store_id: userStoreId } : undefined);
}, [userStoreId, needsStoreId, fetchInitial]);

const fetchMoreOrders = useCallback(() => {
  if (needsStoreId && !userStoreId) return;
  fetchMore(needsStoreId ? { store_id: userStoreId } : undefined);
}, [fetchMore, userStoreId, needsStoreId]);

useEffect(() => {
  if (!isAdmin && responseMeta?.commission) {
    onCommissionData?.(responseMeta.commission);
  }
}, [responseMeta, isAdmin]);

const observerTargetRef = useRef<HTMLDivElement | null>(null);
useEffect(() => {
  if (!hasMore || isLoadingMore || isLoading || !hasFetched) return;
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting && !isLoading) {
        fetchMoreOrders();
      }
    },
    { rootMargin: '150px', threshold: 0.1 }
  );
  if (observerTargetRef.current) observer.observe(observerTargetRef.current);
  return () => observer.disconnect();
}, [hasMore, isLoadingMore, isLoading, hasFetched, fetchMoreOrders]);

if (!hasFetched || isLoading) return <p className="orders-empty">Loading orders...</p>;
if (errorContent) return <div className="orders-empty">{errorContent}</div>;
if (orders.length === 0) return <span className="orders-empty">No orders available yet.</span>;

  return (
    <div className="orders-table-card">

      {/* ── Desktop Header Row ── */}
      <div className="orders-header-row">
        <div className="orders-header-cell ocol--order">ORDER</div>
        <div className="orders-header-cell ocol--customer">CUSTOMER</div>
        <div className="orders-header-cell ocol--financial">FINANCIAL</div>
        <div className="orders-header-cell ocol--fulfilment">FULFILMENT</div>
        {(currentRole==1) ? (<div className="orders-header-cell ocol--type">STORE</div>) : "" }
        <div className="orders-header-cell ocol--sale">SALE</div>
        <div className="orders-header-cell ocol--commission">COMMISSION</div>
      </div>

      {/* ── Order Rows ── */}
      {orders.map((order: any) => {
        const fin = financialLabel(order.financial_status);
        const ful = fulfilmentLabel(order.fulfillment_status || "");
        const isCancelled = !!order.cancelled_at;
        const isCancelledLater = isCancelled && (order.financial_status === 'paid' || order.financial_status === 'partially_refunded');
        const storeName = order.stores && order.stores.length > 0 
          ? order.stores.map((s: any) => s.store_name).filter(Boolean).join(" / ") 
          : undefined;
        const typ = typeLabel(order.type || "", storeName);
        const dateStr = formatDate(order.shopify_createdAt);
        const hasStores = order.stores && order.stores.length > 0;
        const name = customerName(order);

        return (
          <div key={order.id} className={`orders-order-block${isCancelledLater ? " orders-order-block--cancelled-later" : ""}`}>
          <Accordion
            key={order.id}
            disableGutters
            elevation={0}
            className="orders-accordion"
            sx={{ "&:before": { display: "none" } }}
          >
            <AccordionSummary
            expandIcon={<ExpandMoreIcon className="orders-expand-icon" />}
            className="orders-accordion-summary"
            sx={{
            flexDirection: "row-reverse",
            gap: "0px",
            "& .MuiAccordionSummary-content": { margin: "0", width: "100%" },
            "& .MuiAccordionSummary-expandIconWrapper": {
                transform: "none !important",
                transition: "none !important",
                flexShrink: 0,
                marginRight: "8px",
            },
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                transform: "rotate(180deg) !important",
            },
            }}
            >
              <div className="orders-summary-row">

                {/* ORDER col */}
                <div className="orders-data-cell ocol--order">
                  <div className="orders-order-main">
                    <span className="orders-order-number">#{order.shopify_order_number}</span>
                    {(currentRole==1) && (order.shopify_order_status_url) && (
                      <a
                        href={`${import.meta.env.VITE_SHOPIFY_ORDERS_BASE_URL}${order.shopify_order_id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="orders-shopify-link"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Shopify ↗
                      </a>
                    )}
                  </div>
                  <span className="orders-order-date">{dateStr}</span>
                </div>

                <div className="orders-data-cell ocol--customer" title={name}>
                  {name}
                </div>

                <div className="orders-mobile-customer">
                  <span className="orders-mobile-customer-label">Customer</span>
                  <span className="orders-mobile-customer-name">{name}</span>
                </div>

                {/* Mobile badges */}
                <div className="orders-mobile-badges">
                  {isCancelledLater ? (
                    <Tooltip title={CANCELLED_LATER_TOOLTIP} placement="top" arrow>
                      <span className="obadge obadge--sm obadge--cancelled-later">Cancelled · Later</span>
                    </Tooltip>
                  ) : isCancelled ? (
                    <span className="obadge obadge--sm obadge--cancelled">Cancelled</span>
                  ) : null}
                  <span className={`obadge obadge--sm ${fin.cls}`}>{fin.text}</span>
                  <span className={`obadge obadge--sm ${ful.cls}`}>{ful.text}</span>
                  {(currentRole==1) ? (
                    order.type === "hybrid" ? (
                      <span className="orders-type-hybrid-container">
                        <span className="orders-type-store-name">{toTitleCase(storeName ?? "Store")}</span>
                        <span className="orders-type-separator">/</span>
                        <span className="obadge obadge--sm obadge--hybrid">Hybrid</span>
                      </span>
                    ) : (
                      <span className={`obadge obadge--sm ${typ.cls}`}>{typ.text}</span>
                    )
                  ) : "" }
                </div>

                {/* Mobile amounts */}
                <div className="orders-mobile-amounts">
                  <span>
                    Sale <strong>
                      ${order.stores?.length > 0
                        ? order.stores.reduce((sum: number, s: any) => sum + s.store_subtotal, 0).toFixed(2)
                        : Number(order.subtotal ?? 0).toFixed(2)}
                    </strong>
                  </span>
                  <span>Commission <strong>${Number(order.commission ?? 0).toFixed(2)}</strong></span>
                </div>

                {/* Desktop badge cells */}
                <div className="orders-data-cell ocol--financial">
                  {isCancelledLater ? (
                    <Tooltip title={CANCELLED_LATER_TOOLTIP} placement="top" arrow>
                      <span className="obadge obadge--cancelled-later">Cancelled · Later</span>
                    </Tooltip>
                  ) : isCancelled ? (
                    <span className="obadge obadge--cancelled">Cancelled</span>
                  ) : null}
                  <span className={`obadge ${fin.cls}`}>{fin.text}</span>
                </div>
                <div className="orders-data-cell ocol--fulfilment">
                  <span className={`obadge ${ful.cls}`}>{ful.text}</span>
                </div>
                {(currentRole==1) ? 
                (<div className="orders-data-cell ocol--type">
                  {order.type === "hybrid" ? (
                    <span className="orders-type-hybrid-container">
                      <span className="orders-type-store-name">{toTitleCase(storeName ?? "Store")}</span>
                      <span className="orders-type-separator">/</span>
                      <span className="orders-type-hybrid-text">Hybrid</span>
                    </span>
                  ) : (
                    <span className={`${typ.cls}`}>{typ.text}</span>
                  )}
                </div>) : "" }
                
                

                  <div className="orders-data-cell ocol--sale">
                    ${order.stores?.length > 0
                      ? order.stores.reduce((sum: number, s: any) => sum + s.store_subtotal, 0).toFixed(2)
                      : Number(order.subtotal ?? 0).toFixed(2)}
                  </div>

                <div className="orders-data-cell ocol--commission">
                  ${Number(order.commission ?? 0).toFixed(2)}
                </div>

              </div>
            </AccordionSummary>

            {/* ── Expanded Details ── */}
            {hasStores && (
              <AccordionDetails className="orders-accordion-details">
                <div className="orders-items-subheader">
                  <div className="orders-item-img-wrap" /> 
                  <div className="oicol--product">PRODUCT</div>
                  <div className="oicol--qty">UNIT × QTY</div>
                  <div className="oicol--rate">RATE</div>
                  <div className="oicol--icommission">COMMISSION</div>
                </div>

                {order.stores.map((store: any, si: number) => (
                  <div key={si} className="orders-store-group">
                    <div className="orders-store-label">
                      {store.store_name ? (
                        <>
                          <a
                            href={`${import.meta.env.VITE_STORE_BASE_URL}${store.store_id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {store.store_name.toUpperCase()}
                          </a>
                          {` - ${store.item_count} ${store.item_count === 1 ? "ITEM" : "ITEMS"}`}
                        </>
                      ) : `DIRECT - ${store.item_count} ${store.item_count === 1 ? "ITEM" : "ITEMS"}`}
                    </div>

                    {store.items.map((item: any) => {
                      const isRefunded = item.refund_status === 1;
                      const itemFul = fulfilmentLabel(item.fulfillment_status);
                      const rowKey = `${item.shopify_line_item_id}-${item.is_refunded_split ? 'r' : 'a'}`;
                      return (
                        <div
                          key={rowKey}
                          className={`orders-item-row${isRefunded ? " orders-item-row--refunded" : ""}`}
                        >
                          <div className="orders-item-img-wrap">
                            {item.product_image_url ? (
                              <img src={item.product_image_url} alt="" className="orders-item-img" />
                            ) : (
                              <span className="orders-item-img-placeholder">IMG</span>
                            )}
                          </div>
                          <div className="orders-item-info oicol--product">
                            <span className="orders-item-title">{item.product_title}</span>
                            <div className="orders-item-meta">
                              <span className="orders-item-variant">{item.variant_title}</span>
                              <span className={`obadge obadge--sm ${itemFul.cls}`}>{itemFul.text}</span>
                              {isRefunded && (
                                <span className="obadge obadge--sm obadge--refunded-tag">Refunded</span>
                              )}
                            </div>
                          </div>
                          <div className={`oicol--qty${isRefunded ? " orders-cell--strike" : ""}`}>
                            <span className="oicol--qty-mul">${Number(item.unit_price).toFixed(2)} × {item.quantity}</span>
                            {!isRefunded && <span className="oicol--qty-total">${Number(item.line_total).toFixed(2)}</span>}
                          </div>
                          <div className="oicol--rate">
                            {item.commission_rate_snapshot != null && !isRefunded
                              ? `${item.commission_rate_snapshot}%` : "-"}
                          </div>
                          <div className="oicol--icommission">
                            {item.commission_amount != null && !isRefunded
                              ? `$${Number(item.commission_amount).toFixed(2)}` : "-"}
                          </div>
                        </div>
                      );
                    })}

                    <div className="orders-store-footer">
                      <span className="orders-store-footer-item">
                        Subtotal <strong>${store.store_subtotal.toFixed(2)}</strong>
                      </span>
                      <span className="orders-store-footer-item">
                        Commission <strong>${store.store_commission.toFixed(2)}</strong>
                      </span>
                    </div>
                  </div>
                ))}
              </AccordionDetails>
            )}
          </Accordion>
          </div>
        );
      })}

      {/* infinite scroll sentinel */}
      <div ref={observerTargetRef} className="orders-infinite-scroll-trigger" />

      {isLoadingMore && (
        <div className="orders-skeleton-wrapper">
          {[1, 2, 3].map((i) => (
            <div key={i} className="orders-order-block orders-skeleton-row">
              <div className="orders-accordion-summary orders-skeleton-summary">
                <div className="orders-skeleton-line orders-skeleton-line--order" />
                <div className="orders-skeleton-line orders-skeleton-line--badge" />
                <div className="orders-skeleton-line orders-skeleton-line--badge" />
                <div className="orders-skeleton-line orders-skeleton-line--amount" />
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && !isLoadingMore && (
        <div className="orders-load-more-btn-wrapper">
          <button className="orders-load-more-btn" onClick={fetchMoreOrders}>
            Load More
          </button>
        </div>
      )}
    </div>
  );
};

export default OrdersWrapper;
